import React from "react";
import { Row, Col, Tag } from "antd";
import { IUserRecord } from "@/ts/common-types";
import { convertIsoDate } from "@/ts/utils";
import { useUserTimezone } from "@/hooks/AllCustomHooks";
import { DateTimeFormats } from "@/ts/constants";

interface UserDetailsProps {
    user: IUserRecord;
}

const UserDetails: React.FC<UserDetailsProps> = ({ user }) => {
    const currentTimeZone = useUserTimezone(user.timezone);

    return (
        <div className="client-details">
            <div className="card">
                <div className="card-header">
                    <h5 className="mb-0">User Information</h5>
                </div>
                <div className="card-body">
                    <Row gutter={[24, 16]}>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">User ID</div>
                                <div className="value">{user.user_id}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">UID</div>
                                <div className="value">{user.uid}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">First Name</div>
                                <div className="value">{user.first_name}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">Last Name</div>
                                <div className="value">{user.last_name}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">Email</div>
                                <div className="value">{user.email}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">Phone</div>
                                <div className="value">{user.phone || "-"}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">Country</div>
                                <div className="value">
                                    {user.country || "-"}
                                </div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">Timezone</div>
                                <div className="value">
                                    {user.timezone || "-"}
                                </div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">Status</div>
                                <div className="value">
                                    <Tag
                                        color={
                                            user.is_archived
                                                ? "gray"
                                                : user.status
                                                ? "green"
                                                : "red"
                                        }
                                    >
                                        {user.is_archived
                                            ? "Archived"
                                            : user.status
                                            ? "Active"
                                            : "Inactive"}
                                    </Tag>
                                </div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">Super Admin</div>
                                <div className="value">
                                    <Tag
                                        color={
                                            user.is_super_admin
                                                ? "gold"
                                                : "default"
                                        }
                                    >
                                        {user.is_super_admin ? "Yes" : "No"}
                                    </Tag>
                                </div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">Default Client</div>
                                <div className="value">
                                    {user.default_client || "-"}
                                </div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">Expiry Date</div>
                                <div className="value">
                                    {user.expiry_date ? (
                                        <div>
                                            <div>
                                                {convertIsoDate(
                                                    user.expiry_date,
                                                    currentTimeZone
                                                )}
                                            </div>
                                            <div className="small text-muted">
                                                {convertIsoDate(
                                                    user.expiry_date
                                                )}{" "}
                                                {DateTimeFormats.UTC_SUFFIX}
                                            </div>
                                        </div>
                                    ) : (
                                        "-"
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h5 className="mb-0">System Information</h5>
                </div>
                <div className="card-body">
                    <Row gutter={[24, 16]}>
                        {/* Creation Section */}
                        <Col span={12}>
                            <div className="info-section">
                                <h6 className="section-title">Creation</h6>
                                <div className="info-field">
                                    <div className="label">Created At</div>
                                    <div className="value">
                                        <div>
                                            {convertIsoDate(
                                                user.created_at,
                                                currentTimeZone
                                            )}
                                        </div>
                                        <div className="small text-muted">
                                            {convertIsoDate(user.created_at)}{" "}
                                            {DateTimeFormats.UTC_SUFFIX}
                                        </div>
                                    </div>
                                </div>
                                <div className="info-field">
                                    <div className="label">Created By</div>
                                    <div className="value">
                                        {user.created_by_name || "-"}
                                    </div>
                                </div>
                            </div>
                        </Col>

                        {/* Update Section */}
                        <Col span={12}>
                            <div className="info-section">
                                <h6 className="section-title">Last Update</h6>
                                <div className="info-field">
                                    <div className="label">Updated At</div>
                                    <div className="value">
                                        {user.updated_at ? (
                                            <div>
                                                <div>
                                                    {convertIsoDate(
                                                        user.updated_at,
                                                        currentTimeZone
                                                    )}
                                                </div>
                                                <div className="small text-muted">
                                                    {convertIsoDate(
                                                        user.updated_at
                                                    )}{" "}
                                                    {DateTimeFormats.UTC_SUFFIX}
                                                </div>
                                            </div>
                                        ) : (
                                            "-"
                                        )}
                                    </div>
                                </div>
                                <div className="info-field">
                                    <div className="label">Updated By</div>
                                    <div className="value">
                                        {user.updated_by_name || "-"}
                                    </div>
                                </div>
                            </div>
                        </Col>

                        {/* Activity Section */}
                        <Col span={24}>
                            <div className="info-section">
                                <h6 className="section-title">Activity</h6>
                                <div className="info-field">
                                    <div className="label">Last Login</div>
                                    <div className="value">
                                        {user.last_login ? (
                                            <div>
                                                <div>
                                                    {convertIsoDate(
                                                        user.last_login,
                                                        currentTimeZone
                                                    )}
                                                </div>
                                                <div className="small text-muted">
                                                    {convertIsoDate(
                                                        user.last_login
                                                    )}{" "}
                                                    {DateTimeFormats.UTC_SUFFIX}
                                                </div>
                                            </div>
                                        ) : (
                                            "Never logged in"
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
};

export default UserDetails;
